import { useRef, useState } from 'react'

import { useApolloClient, useMutation } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'

import { useAuth } from 'src/auth'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  START_VOE_OUTREACH,
  VOE_OUTREACH_PREVIEW,
} from 'src/graphql/voe.graphql'
import { useCompanyOutreachConfig } from 'src/hooks/useCompanyOutreachConfig'
import { buildVoEInstructionsEmailBody } from 'src/pages/ApplicantDetailsPage/tabs/VoETab/lib/voeEmailTemplate'

import AutomatedOutreachPanel from './AutomatedOutreachPanel'
import {
  opensPanel,
  panelState,
  type OutreachSchedulePreview,
} from './outreachSchedule'

export interface OutreachEmailTarget {
  voeId: string
  email?: string | null
  companyName?: string | null
  applicantName: string
}

type PanelOutcome =
  | 'started'
  | 'saved'
  | 'scheduled_more'
  | 'started_override'
  | 'send_manually'
  | 'dismissed'

export interface SendManuallyOptions {
  /** The address typed in the panel, when the recruiter entered one. */
  email?: string
}

const SUBMIT_RESULT: Record<string, { toast: string; outcome: PanelOutcome }> =
  {
    READY: { toast: 'Automated outreach started', outcome: 'started' },
    RUNNING: { toast: 'Email address saved', outcome: 'saved' },
    ENDED: { toast: 'More attempts scheduled', outcome: 'scheduled_more' },
    BLOCKED: {
      toast: 'Automated outreach started',
      outcome: 'started_override',
    },
  }

/**
 * Puts the outreach panel in front of a VoE's manual email composer. When
 * the company has outreach set up, the email icon opens the panel; with none,
 * or if the check fails, it opens the composer as before.
 */
export const useAutomatedOutreachEmail = ({
  component,
  onStarted,
}: {
  /** Reported to RUM to tell the entry points apart. */
  component: string
  /** After the address is handed to automation, e.g. to refetch a list. */
  onStarted?: () => void
}) => {
  const client = useApolloClient()
  const { currentUser } = useAuth()
  const { config } = useCompanyOutreachConfig()
  const [state, setState] = useState<{
    target: OutreachEmailTarget
    preview: OutreachSchedulePreview
    sendManually: (options: SendManuallyOptions) => void
  } | null>(null)
  const checkingRef = useRef(false)

  const [startVoEOutreach, { loading }] = useMutation(START_VOE_OUTREACH)

  const report = (
    name: string,
    target: OutreachEmailTarget,
    preview: OutreachSchedulePreview,
    extra: Record<string, unknown> = {}
  ) =>
    datadogRum.addAction(name, {
      surface: 'dashboard',
      component,
      voe_id: target.voeId,
      state: panelState(preview),
      blocked_reason: preview.blockedReason ?? null,
      started: preview.started,
      step_count: preview.steps.length,
      ...extra,
    })

  const requestEmail = async (
    target: OutreachEmailTarget,
    {
      sendManually,
      notAutomated = () => sendManually({}),
    }: {
      sendManually: (options: SendManuallyOptions) => void
      notAutomated?: () => void
    }
  ) => {
    // Only a loaded config that says off skips the check. While it is still
    // loading (null) the server decides, so an early click is not sent by hand.
    if (config?.enabled === false) {
      notAutomated()
      return
    }
    // One check per click: a second click while it runs is ignored.
    if (checkingRef.current) return
    checkingRef.current = true

    let preview: OutreachSchedulePreview | null = null
    try {
      const { data } = await client.query({
        query: VOE_OUTREACH_PREVIEW,
        variables: { voeId: target.voeId },
        fetchPolicy: 'network-only',
      })
      preview = data?.voeOutreachPreview ?? null
    } catch {
      // Never block the recruiter from emailing because the check failed.
    } finally {
      checkingRef.current = false
    }

    if (!opensPanel(preview)) {
      notAutomated()
      return
    }
    setState({ target, preview, sendManually })
    report('voe_email_panel_shown', target, preview)
  }

  const close = (outcome: PanelOutcome) => {
    if (state)
      report('voe_email_panel_decided', state.target, state.preview, {
        outcome,
      })
    setState(null)
  }

  const handleSubmit = async (email: string) => {
    if (!state) return
    const { target, preview } = state
    const current = panelState(preview)
    const unchanged = email === (target.email ?? '').trim()
    if (current === 'RUNNING' && unchanged) {
      close('dismissed')
      return
    }
    try {
      await startVoEOutreach({ variables: { voeId: target.voeId, email } })
      const done = SUBMIT_RESULT[current] ?? SUBMIT_RESULT.READY
      toast(done.toast, 'success')
      close(done.outcome)
      onStarted?.()
    } catch (error) {
      toast(error?.message || 'Could not start automated outreach', 'error')
    }
  }

  const companyName = currentUser?.companyName ?? ''
  const sender = state?.preview.sender
  const employerName = state?.target.companyName || 'employer'

  const panel = state ? (
    <AutomatedOutreachPanel
      key={state.target.voeId}
      opened
      employerName={employerName}
      applicantName={state.target.applicantName}
      initialEmail={state.target.email ?? ''}
      preview={state.preview}
      email={{
        companyName,
        subject: `Employment verification request from ${companyName}`,
        body: buildVoEInstructionsEmailBody({
          companyName,
          applicantName: state.target.applicantName || 'a driver',
          pastEmployerName: state.target.companyName ?? '',
          recruiterEmail: sender?.email ?? '',
          companyPhone: sender?.phone ?? '',
        }),
        attachments: [
          `Safety Performance History Records Request - ${employerName}.pdf`,
          'Drivers Employment Application Consent.pdf',
        ],
      }}
      loading={loading}
      onClose={() => close('dismissed')}
      onSubmit={handleSubmit}
      onSendManually={(email) => {
        const { sendManually } = state
        close('send_manually')
        sendManually({ email: email || undefined })
      }}
    />
  ) : null

  return { requestEmail, panel }
}
